// The submission flow's calls to the ProfileManagement (PM) API: photos and resumes go from the
// browser straight to PM with the token verify-email returned (decided 2026-10-06, docs/14 and
// docs/26). Registration never sees the files; it records which photo slot and resume id a
// submission uses, and checks them with SQL before it counts step 2 as done.
//
// One photo and one resume of the flow's own: the first upload adds it, every later upload in
// this flow replaces that same slot or file, so re-cropping or changing your mind never fills
// the photo library (a free plan holds 2). Photos and resumes the flow didn't upload are never
// replaced or deleted from here.

import i18next from "i18next";
import { getSubmissionAuth } from "../../../helpers/submissionAuth";
import { NETWORK_ERROR_MESSAGE_KEY } from "../../http";
import { IMediaAssetDto, ISubmissionApiError, SubmissionApiErrorCode } from "./types";

// PM's limits (docs/14): photos up to 10 MB; PDF resumes up to 5 MB.
export const PM_PHOTO_MAX_BYTES = 10 * 1024 * 1024;
export const PM_RESUME_MAX_BYTES = 5 * 1024 * 1024;

const OWN_UPLOADS_KEY = "sfPmUploads.v1";

interface IPmPhoto {
  url: string;
  isDefault: boolean;
  photoNumber: number;
}

interface IPmProfile {
  rosterId: number;
  isSelfRepresent: boolean;
  isDefault: boolean;
  defaultPhotoNumber?: number;
}

interface IPmResume {
  resumeId: number;
  rosterId: number;
  name: string;
  visible: boolean;
  pdfUrl?: string;
  lastModified?: string;
  // "Pdf", or "Simple" / "Editor" for one typed on theWorkbook (PM's ResumeTypeDTO, as a string).
  type?: string;
  pdfPageCount?: number | null;
}

// Always HTTP 200: the outcome is in the flags (docs/26, C1).
interface IPmPhotoUploadResult {
  success: boolean;
  error?: boolean;
  message?: string;
  photoNumber?: number;
  tooLarge?: boolean;
  maxNumberOfPhotosExceed?: boolean;
  invalidFile?: boolean;
}

interface IOwnUploads {
  clientId: number;
  photoNumber?: number;
  resumeId?: number;
}

const pmBase = () => (process.env.REACT_APP_PM_Api || "").replace(/\/+$/, "");

const fail = (status: number, errorCode: SubmissionApiErrorCode, message = ""): ISubmissionApiError => ({
  status,
  errorCode,
  message,
});

const account = () => {
  const auth = getSubmissionAuth();
  if (!auth || !auth.jwt || !auth.clientId) throw fail(401, "unauthorized");
  return auth;
};

// fetch with the token; a network failure becomes the shape the model already understands.
const pmFetch = async (path: string, init: RequestInit = {}): Promise<Response> => {
  const auth = account();
  try {
    return await fetch(`${pmBase()}${path}`, {
      ...init,
      mode: "cors",
      headers: { Authorization: auth.jwt, ...(init.headers || {}) },
    });
  } catch (e) {
    const err: any = new Error(i18next.t(NETWORK_ERROR_MESSAGE_KEY));
    err.isNetworkError = true;
    throw err;
  }
};

const pmJson = async <T>(path: string, init?: RequestInit): Promise<T> => {
  const resp = await pmFetch(path, init);
  if (resp.status === 401 || resp.status === 403) throw fail(resp.status, "unauthorized");
  if (!resp.ok) throw fail(resp.status, "uploadFailed");
  return (await resp.json()) as T;
};

// --- the flow's own uploads (per tab, like the rest of the flow's state) -------------------

const readOwn = (clientId: number): IOwnUploads => {
  try {
    const raw = window.sessionStorage.getItem(OWN_UPLOADS_KEY);
    const own = raw ? (JSON.parse(raw) as IOwnUploads) : null;
    return own && own.clientId === clientId ? own : { clientId };
  } catch (e) {
    return { clientId };
  }
};

const writeOwn = (own: IOwnUploads) => {
  try {
    window.sessionStorage.setItem(OWN_UPLOADS_KEY, JSON.stringify(own));
  } catch (e) {
    // Without storage a refresh forgets the slot, and the next upload adds a new one.
  }
};

// --- profiles ---------------------------------------------------------------------------

let profileCache: { clientId: number; profile: IPmProfile | null } | null = null;

// The profile step 2 works with: the self-represented one, else the main one (docs/14). New
// resumes go to it, and its default photo is the actor's main headshot.
const getFlowProfile = async (): Promise<IPmProfile | null> => {
  const { clientId } = account();
  if (profileCache && profileCache.clientId === clientId) return profileCache.profile;
  const profiles = await pmJson<IPmProfile[]>(`/ActorProfile/${clientId}/all`);
  const chosen =
    profiles.filter((p) => p.isSelfRepresent)[0] || profiles.filter((p) => p.isDefault)[0] || profiles[0] || null;
  profileCache = { clientId, profile: chosen };
  return chosen;
};

export const getUploadRosterId = async (): Promise<number> => {
  const profile = await getFlowProfile();
  if (!profile) throw fail(409, "uploadFailed");
  return profile.rosterId;
};

// --- photos -----------------------------------------------------------------------------

// The image service caches each URL for a minute; a slot replaced in this flow keeps its URL,
// so the version makes the new picture show at once.
const withVersion = (url: string) => {
  if (!url) return url;
  const absolute = url.indexOf("//") === 0 ? `${window.location.protocol}${url}` : url;
  return `${absolute}${absolute.indexOf("?") > -1 ? "&" : "?"}v=${Date.now()}`;
};

// PM lists a photo's URL at the image service's default size, 125 x 156, which the flow's photo
// boxes stretch. thj=j is the large one (576 x 720; t is the thumbnail, h 250 x 313).
const largePhoto = (url: string) => {
  if (!url) return url;
  const bare = url.replace(/([?&])thj=[^&]*&?/i, "$1").replace(/[?&]$/, "");
  return `${bare}${bare.indexOf("?") > -1 ? "&" : "?"}thj=j`;
};

const photoAsset = (photo: IPmPhoto, fileName?: string, sizeBytes = 0): IMediaAssetDto => ({
  assetId: photo.photoNumber,
  kind: "photo",
  fileName: fileName || i18next.t("Photo {{number}}", { number: photo.photoNumber }),
  url: withVersion(largePhoto(photo.url)),
  sizeBytes,
  uploadedAtUtc: new Date().toISOString(),
});

// The actor's photos, the main headshot marked. `isDefault` on the photo list can't say which
// it is (it marks the first photo when none is set, docs/26 C3): the profile's default can.
export const listPhotos = async (): Promise<IMediaAssetDto[]> => {
  const { clientId } = account();
  const photos = await pmJson<IPmPhoto[]>(`/Photo/${clientId}`);
  let main: number | undefined;
  try {
    const profile = await getFlowProfile();
    main = profile && profile.defaultPhotoNumber ? profile.defaultPhotoNumber : undefined;
  } catch (e) {
    main = undefined; // no main photo marked; the list still shows
  }
  return photos.map((p) => ({ ...photoAsset(p), isMain: p.photoNumber === main }));
};

export const findPhoto = async (photoNumber: number, fileName?: string): Promise<IMediaAssetDto | undefined> => {
  const { clientId } = account();
  const photos = await pmJson<IPmPhoto[]>(`/Photo/${clientId}`);
  const photo = photos.filter((p) => p.photoNumber === photoNumber)[0];
  return photo ? photoAsset(photo, fileName) : undefined;
};

const photoError = (result: IPmPhotoUploadResult): ISubmissionApiError =>
  result.tooLarge
    ? fail(400, "fileTooLarge", result.message)
    : result.maxNumberOfPhotosExceed
    ? fail(409, "photoLimitReached", result.message)
    : result.invalidFile
    ? fail(400, "unsupportedFileType", result.message)
    : fail(500, "uploadFailed", result.message);

export const uploadPhoto = async (file: Blob, fileName: string): Promise<IMediaAssetDto> => {
  if (file.size > PM_PHOTO_MAX_BYTES) throw fail(400, "fileTooLarge");
  const { clientId } = account();
  const own = readOwn(clientId);

  const send = async (method: "POST" | "PUT", path: string) => {
    const form = new FormData();
    form.append("formFile", file, fileName || "photo.jpg");
    return pmJson<IPmPhotoUploadResult>(path, { method, body: form });
  };

  // Replace the flow's own slot while it still holds a photo; otherwise add one.
  let result: IPmPhotoUploadResult | null = null;
  if (own.photoNumber) {
    const current = await findPhoto(own.photoNumber);
    if (current) result = await send("PUT", `/Photo/${clientId}/num/${own.photoNumber}`);
  }
  if (!result) result = await send("POST", `/Photo/${clientId}`);
  if (!result.success || !result.photoNumber) throw photoError(result);

  writeOwn({ ...own, clientId, photoNumber: result.photoNumber });
  // The upload answer carries no URL (docs/26, C2): read it back.
  const uploaded = await findPhoto(result.photoNumber, fileName);
  return uploaded || photoAsset({ photoNumber: result.photoNumber, url: "", isDefault: false }, fileName, file.size);
};

// --- resumes ----------------------------------------------------------------------------

// Never asked of the visitor (decided 2026-10-04): a fixed title in the page's language.
export const resumeTitle = (language: string = i18next.language || "en") => {
  const lang = language.toLowerCase();
  if (lang.indexOf("fr") === 0) return "CV";
  if (lang.indexOf("es") === 0) return "Currículum";
  return "Resume";
};

const resumeAsset = (resume: IPmResume, fileName?: string, sizeBytes = 0): IMediaAssetDto => ({
  assetId: resume.resumeId,
  kind: "resume",
  fileName: fileName || resume.name,
  url: resume.pdfUrl,
  sizeBytes,
  uploadedAtUtc: resume.lastModified || new Date().toISOString(),
  resumeType: resume.type ? (String(resume.type).toLowerCase() === "pdf" ? "pdf" : "typed") : undefined,
  pageCount: resume.pdfPageCount || undefined,
});

// Every visible resume, from every profile, once each (the list can repeat typed resumes), the
// main one marked. PM has no main resume, so it is the newest on the profile the flow submits
// through, or the newest of all when that profile has none.
export const listResumes = async (): Promise<IMediaAssetDto[]> => {
  const { clientId } = account();
  const resumes = await pmJson<IPmResume[]>(`/Resume/${clientId}`);
  const seen: { [id: number]: boolean } = {};
  const visible = resumes
    .filter((r) => r.visible)
    .filter((r) => (seen[r.resumeId] ? false : (seen[r.resumeId] = true)));
  let rosterId: number | undefined;
  try {
    const profile = await getFlowProfile();
    rosterId = profile ? profile.rosterId : undefined;
  } catch (e) {
    // Without the profile, the newest of all.
  }
  const newest = (list: IPmResume[]) =>
    list.slice().sort((a, b) => (Date.parse(b.lastModified || "") || 0) - (Date.parse(a.lastModified || "") || 0))[0];
  const main = newest(visible.filter((r) => r.rosterId === rosterId)) || newest(visible);
  return visible.map((r) => ({ ...resumeAsset(r), isMain: !!main && r.resumeId === main.resumeId }));
};

export const findResume = async (resumeId: number, fileName?: string): Promise<IMediaAssetDto | undefined> => {
  const { clientId } = account();
  const resumes = await pmJson<IPmResume[]>(`/Resume/${clientId}`);
  const resume = resumes.filter((r) => r.resumeId === resumeId)[0];
  return resume ? resumeAsset(resume, fileName) : undefined;
};

const isPdf = (fileName: string, mimeType?: string) =>
  /\.pdf$/i.test(fileName || "") || (mimeType || "").toLowerCase() === "application/pdf";

export const uploadResume = async (file: Blob, fileName: string, mimeType?: string): Promise<IMediaAssetDto> => {
  if (!isPdf(fileName, mimeType)) throw fail(400, "unsupportedFileType");
  if (file.size > PM_RESUME_MAX_BYTES) throw fail(400, "fileTooLarge");
  const { clientId } = account();
  const rosterId = await getUploadRosterId();
  const own = readOwn(clientId);
  // PM accepts only names ending in .pdf.
  const pdfName = /\.pdf$/i.test(fileName) ? fileName : `${fileName || "resume"}.pdf`;

  const form = new FormData();
  form.append("formFile", file, pdfName);

  let resp: Response | null = null;
  if (own.resumeId && (await findResume(own.resumeId))) {
    resp = await pmFetch(`/Resume/${clientId}/roster/${rosterId}/resume/${own.resumeId}/file`, { method: "PATCH", body: form });
  }
  if (!resp) {
    const title = encodeURIComponent(resumeTitle());
    resp = await pmFetch(`/Resume/${clientId}/roster/${rosterId}?visibility=true&title=${title}`, { method: "POST", body: form });
  }

  if (resp.status === 401 || resp.status === 403) throw fail(resp.status, "unauthorized");
  if (resp.status === 400) throw fail(400, "unsupportedFileType");
  if (!resp.ok) throw fail(resp.status, "uploadFailed");

  const saved = (await resp.json()) as IPmResume;
  writeOwn({ ...own, clientId, resumeId: saved.resumeId });
  return resumeAsset(saved, fileName, file.size);
};

// --- representation (decided 2026-10-07, docs/30 §9) ------------------------------------
//
// Agents are added in PM, as ActorPortal does it: a new roster (the self-represented one stays);
// an agent PM doesn't know is created there, pending, and PM emails them the approval link.
// Registration is then told the roster, for the submission on screen only
// (POST Signup/submission/{submissionId}/representation).

interface IPmAgencySearch {
  records: { agencyId: number; name: string; city?: string; province?: string; allowNewAgents?: boolean }[];
}

interface IPmAgent {
  agentId: number;
  firstName?: string;
  lastName?: string;
}

interface IPmRosterAgent {
  rosterId: number;
  agentId: number;
  agencyId: number;
}

export interface IPmNewAgent {
  agencyId?: number;
  agencyName?: string;
  agentId?: number;
  agentFirstName?: string;
  agentLastName?: string;
  email?: string;
  phone?: string;
}

export const searchAgencies = async (name: string) => {
  const found = await pmJson<IPmAgencySearch>(`/AgencyAgent/search?name=${encodeURIComponent(name)}&pageSize=20`);
  return (found.records || []).map((a) => ({
    agencyId: a.agencyId,
    agencyName: a.name,
    cityName: [a.city, a.province].filter((part) => !!part).join(", ") || undefined,
    allowNewAgents: a.allowNewAgents !== false,
  }));
};

export const listAgencyAgents = async (agencyId: number) => {
  const agents = await pmJson<IPmAgent[]>(`/AgencyAgent/${agencyId}`);
  return (agents || [])
    .map((a) => ({ agentId: a.agentId, agentName: `${a.firstName || ""} ${a.lastName || ""}`.trim() }))
    .filter((a) => !!a.agentName);
};

// PM's messages are English only, so the page says its own: by status.
//   409  the agency takes no new agents from here, or the email is on more than one account
//   400  details missing or not valid, or the actor is at PM's limit of profiles
export const addAgent = async (agent: IPmNewAgent): Promise<IPmRosterAgent> => {
  const { clientId } = account();
  const resp = await pmFetch(`/ClientAgent/${clientId}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ...agent, agencyId: agent.agencyId || 0, forStudio: false }),
  });
  if (resp.status === 401 || resp.status === 403) throw fail(resp.status, "unauthorized");
  if (resp.status === 409) throw fail(409, "agentNotAdded");
  if (resp.status === 400) throw fail(400, "invalidRepresentation");
  if (!resp.ok) throw fail(resp.status, "representationFailed");
  // An agent the actor already has comes back with a warning and the same roster: still a success.
  const roster = (await resp.json()) as IPmRosterAgent;
  if (!roster || !roster.rosterId) throw fail(500, "representationFailed");
  return roster;
};
